import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Lock, AlertCircle, CheckCircle } from 'lucide-react';
import { resetPassword } from '../services/api';
import { WhatsAppHelpText } from '../components/WhatsAppButton';

export default function ResetPasswordPage() {
  const { token } = useParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setIsLoading(true);
    try {
      await resetPassword(token, password);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired reset link. Please request a new one.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white p-8" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8">
          <img src="/logo.png" alt="JustDeliver" className="h-12 w-auto" />
        </div>

        {success ? (
          <Card className="border border-gray-200 shadow-none">
            <CardContent className="pt-8 pb-8 text-center">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-[#2E7D32]" />
              </div>
              <h2 className="text-xl font-bold font-['Inter'] text-gray-900 mb-3">Password reset!</h2>
              <p className="text-gray-600 mb-6">Your password has been updated. You can now log in.</p>
              <Link to="/login">
                <Button className="w-full bg-[#2E7D32] text-white hover:bg-[#1B5E20]" data-testid="reset-login-btn">
                  Go to Login
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <Card className="border border-gray-200 shadow-none">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="text-2xl font-semibold font-['Inter']">Reset password</CardTitle>
              <CardDescription className="text-gray-600">
                Enter your new password
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="reset-error">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm font-medium">New Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 rounded-sm"
                      required
                      minLength={6}
                      data-testid="reset-password-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-sm font-medium">Confirm Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="confirmPassword"
                      type="password"
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="pl-10 rounded-sm"
                      required
                      data-testid="reset-confirm-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-[#2E7D32] text-white hover:bg-[#1B5E20] rounded-sm h-11"
                  disabled={isLoading}
                  data-testid="reset-submit-btn"
                >
                  {isLoading ? 'Resetting...' : 'Reset Password'}
                </Button>
                <WhatsAppHelpText />
              </form>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
